import React from 'react';
import { DocPageLayout } from '../../components/DocPageLayout';
import { CodeBlock } from '../../components/CodeBlock';
import { InfoBox } from '../../components/InfoBox';
export function ApiBrowserPaymentPage() {
  return <DocPageLayout title="Browser Payment API" description="Browser payment methods allow a payer to pay for goods and services online on the browser payment provider's website." breadcrumbs={[{
    label: 'API Reference',
    id: 'api-overview'
  }, {
    label: 'Browser Payment'
  }]} sections={[{
    id: 'initiate-browser-payment',
    title: 'Initiate Browser Payment'
  }, {
    id: 'confirm-browser-payment',
    title: 'Confirm Browser Payment'
  }, {
    id: 'retrieve-browser-payment',
    title: 'Retrieve Browser Payment'
  }]} data-id="element-483">
      <p className="text-gray-700 mb-8" data-id="element-484">
        Integrate with browser-based payment providers such as PayPal, Alipay,
        UnionPay SecurePay, and other redirect-based payment methods.
      </p>

      <h2 id="initiate-browser-payment" className="text-2xl font-bold text-gray-900 mt-10 mb-4 border-b border-gray-200 pb-2" data-id="element-485">
        Initiate Browser Payment
      </h2>
      <div className="bg-gray-50 p-4 rounded-lg border border-gray-200 mb-6 font-mono text-sm" data-id="element-486">
        <span className="inline-block px-2 py-0.5 rounded-full text-xs font-bold bg-amber-100 text-amber-700 mr-2" data-id="element-487">
          PUT
        </span>
        <span className="text-gray-600" data-id="element-488">/merchant/</span>
        <span className="text-blue-600" data-id="element-489">{'{merchantId}'}</span>
        <span className="text-gray-600" data-id="element-490">/order/</span>
        <span className="text-blue-600" data-id="element-491">{'{orderId}'}</span>
        <span className="text-gray-600" data-id="element-492">/transaction/</span>
        <span className="text-blue-600" data-id="element-493">{'{transactionId}'}</span>
      </div>
      <CodeBlock language="JSON" title="Request" code={`{
  "apiOperation": "INITIATE_BROWSER_PAYMENT",
  "browserPayment": {
    "operation": "PAY",
    "paymentConfirmation": "CONFIRM_AT_PROVIDER",
    "returnUrl": "https://your-site.com/payment-result"
  },
  "order": {
    "amount": "75.00",
    "currency": "USD",
    "description": "Order #12345"
  },
  "sourceOfFunds": {
    "type": "BROWSER_PAYMENT",
    "provided": {
      "browserPayment": {
        "paymentMethod": "PAYPAL"
      }
    }
  }
}`} data-id="element-494" />

      <h2 id="confirm-browser-payment" className="text-2xl font-bold text-gray-900 mt-10 mb-4 border-b border-gray-200 pb-2" data-id="element-495">
        Confirm Browser Payment
      </h2>
      <div className="bg-gray-50 p-4 rounded-lg border border-gray-200 mb-6 font-mono text-sm" data-id="element-496">
        <span className="inline-block px-2 py-0.5 rounded-full text-xs font-bold bg-amber-100 text-amber-700 mr-2" data-id="element-497">
          PUT
        </span>
        <span className="text-gray-600" data-id="element-498">/merchant/</span>
        <span className="text-blue-600" data-id="element-499">{'{merchantId}'}</span>
        <span className="text-gray-600" data-id="element-500">/order/</span>
        <span className="text-blue-600" data-id="element-501">{'{orderId}'}</span>
        <span className="text-gray-600" data-id="element-502">/transaction/</span>
        <span className="text-blue-600" data-id="element-503">{'{transactionId}'}</span>
      </div>
      <p className="text-gray-700 mb-4" data-id="element-504">
        After the payer completes authentication on the provider's site, confirm
        the payment to finalize the transaction.
      </p>

      <h2 className="text-2xl font-bold text-gray-900 mt-10 mb-4" data-id="element-505">
        Supported Providers
      </h2>
      <div className="overflow-x-auto mb-8" data-id="element-506">
        <table className="w-full text-sm border border-gray-200 rounded-lg overflow-hidden" data-id="element-507">
          <thead data-id="element-508">
            <tr className="bg-gray-100" data-id="element-509">
              <th className="text-left px-4 py-3 font-semibold border-b" data-id="element-510">
                Provider
              </th>
              <th className="text-left px-4 py-3 font-semibold border-b" data-id="element-511">
                Payment Method Value
              </th>
              <th className="text-left px-4 py-3 font-semibold border-b" data-id="element-512">
                Currencies
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200" data-id="element-513">
            <tr data-id="element-514">
              <td className="px-4 py-3" data-id="element-515">PayPal</td>
              <td className="px-4 py-3 font-mono text-xs" data-id="element-516">PAYPAL</td>
              <td className="px-4 py-3 text-gray-600" data-id="element-517">
                USD, EUR, GBP, AUD, CAD
              </td>
            </tr>
            <tr className="bg-gray-50" data-id="element-518">
              <td className="px-4 py-3" data-id="element-519">Alipay</td>
              <td className="px-4 py-3 font-mono text-xs" data-id="element-520">ALIPAY</td>
              <td className="px-4 py-3 text-gray-600" data-id="element-521">CNY, USD</td>
            </tr>
            <tr data-id="element-522">
              <td className="px-4 py-3" data-id="element-523">UnionPay SecurePay</td>
              <td className="px-4 py-3 font-mono text-xs" data-id="element-524">UNION_PAY</td>
              <td className="px-4 py-3 text-gray-600" data-id="element-525">CNY, USD, EUR</td>
            </tr>
            <tr className="bg-gray-50" data-id="element-526">
              <td className="px-4 py-3" data-id="element-527">giropay</td>
              <td className="px-4 py-3 font-mono text-xs" data-id="element-528">GIROPAY</td>
              <td className="px-4 py-3 text-gray-600" data-id="element-529">EUR</td>
            </tr>
            <tr data-id="element-530">
              <td className="px-4 py-3" data-id="element-531">Sofort</td>
              <td className="px-4 py-3 font-mono text-xs" data-id="element-532">SOFORT</td>
              <td className="px-4 py-3 text-gray-600" data-id="element-533">EUR</td>
            </tr>
          </tbody>
        </table>
      </div>
      <InfoBox type="tip" title="Return URL" data-id="element-534">
        Always provide a valid returnUrl. The payer will be redirected to this
        URL after completing or cancelling the payment on the provider's site.
      </InfoBox>
    </DocPageLayout>;
}